import { getLocale } from 'umi';
import { urlToMap } from './url';

export const statsDetailIdKey = 'statsDetailId';

// 下载参数缓存前缀
export const downloadParamsPrefix = 'downLoadParams';
// 下转参数缓存前缀
export const statsDetailParamsPrefix = 'statsDetailParams';
// 缓存过期时间：3小时
const STORAGE_EXPIRE_TIME = 1000 * 60 * 60 * 3;
// 二级清理时保留的最新条数
const KEEP_LATEST_COUNT = 2;

export function getStatsDetailParams() {
  try {
    const map = urlToMap(window.location.href);
    if (map.get(statsDetailIdKey)) {
      const statsDetailParams =
        sessionStorage.getItem(
          `statsDetailParams-${map.get(statsDetailIdKey)}`,
        ) || 'null';

      return JSON.parse(statsDetailParams);
    }
  } catch (error) {}

  return null;
}

export function setStatsDetailParams(params: any, id: string) {
  const filter = {
    ...(params || {}),
    goTime: new Date().getTime(),
  };

  // 后续转为带有标识的
  return safeSetItem(
    sessionStorage,
    `${statsDetailParamsPrefix}-${id}`,
    JSON.stringify(filter),
    statsDetailParamsPrefix,
  );
}

export const getGPTDateType = (key: string) => {
  const DailyToDay: any = {
    daily: 'Day',
    '7days': 'Week',
    '30days': 'Month',
  };

  return DailyToDay[key] || 'Day';
};

export const getGptStoreKey = (kind: number, edition_unified_id: string) => {
  return `0907-OpinionNewMarkdownFilter-${kind}-${edition_unified_id}`;
};

export const setGPTStore = (
  value: {
    start_time: string;
    end_time: string;
    date_type: string;
    language: string;
  },
  key: any,
) => {
  const { start_time, end_time, date_type, language } = value;
  const data = {
    lang: language,
    list: {
      currentTime: [start_time, end_time],
      dateTab: getGPTDateType(date_type),
    },
  };

  sessionStorage.setItem(key, JSON.stringify(data));
};

type PrefixStorageInfo = { key: string; time: number };

/**
 * 收集带前缀的缓存信息（key + 写入时间）
 *
 * 兼容新旧两种缓存格式，时间字段按优先级取：
 * 1. storedAt     —— 新缓存（localParamsStorage 写入的 { id, data, storedAt, expiresAt } 包裹结构）
 * 2. downloadTime —— 老下载参数缓存
 * 3. goTime       —— 老下钻参数缓存
 * 缺失时按 0 处理（视为最早）
 */
function getPrefixStorageList(
  storage: Storage,
  prefix: string,
): PrefixStorageInfo[] {
  const list: PrefixStorageInfo[] = [];

  try {
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (!key?.startsWith(prefix)) continue;

      let time = 0;
      try {
        const data = JSON.parse(storage.getItem(key) || '{}');
        time = data?.storedAt || data?.downloadTime || data?.goTime || 0;
      } catch (error) {}

      list.push({ key, time });
    }
  } catch (error) {}

  return list;
}

/** 一级清理：删除超过3小时的旧缓存 */
function clearExpiredStorage(storage: Storage, prefix: string) {
  try {
    const now = new Date().getTime();
    getPrefixStorageList(storage, prefix).forEach(({ key, time }) => {
      if (now - time > STORAGE_EXPIRE_TIME) {
        storage.removeItem(key);
      }
    });
  } catch (error) {}
}

/** 二级清理：只保留最新的2条 */
function keepLatestStorage(storage: Storage, prefix: string) {
  try {
    getPrefixStorageList(storage, prefix)
      .sort((a, b) => b.time - a.time)
      .slice(KEEP_LATEST_COUNT)
      .forEach(({ key }) => storage.removeItem(key));
  } catch (error) {}
}

/** 三级清理：删除该前缀的全部缓存 */
function clearPrefixStorage(storage: Storage, prefix: string) {
  try {
    getPrefixStorageList(storage, prefix).forEach(({ key }) =>
      storage.removeItem(key),
    );
  } catch (error) {}
}

/**
 * 安全写入：
 * 每次写入前主动清理超过3小时的旧缓存；
 * 若写入失败，依次降级：
 * 1级：只保留最新2条后重试
 * 2级：清空该前缀全部缓存后重试
 * 3级：alert提示用户无法写入缓存
 * @returns 是否写入成功
 */
export function safeSetItem(
  storage: Storage,
  key: string,
  value: string,
  prefix: string,
): boolean {
  const trySet = () => {
    try {
      storage.setItem(key, value);
      return true;
    } catch (error) {
      return false;
    }
  };

  // 每次写入前主动清理超过3小时的旧缓存，避免过期缓存堆积
  clearExpiredStorage(storage, prefix);

  if (trySet()) return true;

  // 1级：只保留最新2条
  keepLatestStorage(storage, prefix);
  if (trySet()) return true;

  // 2级：清空该前缀的全部缓存
  clearPrefixStorage(storage, prefix);
  if (trySet()) return true;

  // 3级：提示用户无法写入缓存
  const isEn = getLocale() === 'en-US';
  window.alert(
    isEn
      ? 'Insufficient browser storage space. Please clear the site data and try again.'
      : '浏览器缓存空间不足，请清理站点数据后重试',
  );
  return false;
}

export function clearDownloadStorage() {
  clearExpiredStorage(localStorage, downloadParamsPrefix);
}

export function clearGoPageStorage() {
  clearExpiredStorage(sessionStorage, statsDetailParamsPrefix);
}

export function opinionRankShow(userInfo: any) {
  const key = `opinion-rank-show-${userInfo?.userid}`;

  return {
    set: (value: boolean) => {
      try {
        localStorage.setItem(key, String(value));
      } catch (error) {}
    },
    get: (hide?: boolean): boolean => {
      if (hide) {
        return false;
      }
      const value = localStorage.getItem(key) || 'true';
      return value === 'true';
    },
  };
}
